import { axiosClient as client } from "auth-sdk";
import { ConnectionItem, Flex, Image, Text } from "@wayflyer/flyui";
import type { ConnectionSchemas } from "api-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { connectByPath } from "../../../data-sources";
import { useCustomerAppConfig } from "undercarriage";

import { usePlatformConnectorProperties, useCompleteConnectionProps } from "../../queries";
import { useConnectionFlowsConfig } from "../../config";
import { COMMON_MESSAGES } from "../../common-messages";
import { ConnectionsList } from "../../common/connections";
import { ConnectorPropertiesLoading } from "../../common/connector-properties-loading";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { PropertySelector } from "../../common/property-selector";
import { MESSAGES } from "../../messages";

const CONNECTOR_SLUG = "facebook2";
const CONNECTOR_NAME = "Meta Ads";

const MetaAdsConnectContent = ({ onInitiateConnection }: { onInitiateConnection?: () => void }) => {
  const intl = useIntl();
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { connectorsApiUrl, defaultCallbackPath } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    defaultCallbackPath,
    connectorsApiUrl
  );

  return (
    <Flex direction="column" gap="8">
      <Flex direction="column" gap="8" align="flex-start" justify="space-between">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.marketingDescription)}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          connectForm={
            <ConnectionItem
              logo={
                connectorProperties.connectionLogoSrc ? <Image src={connectorProperties.connectionLogoSrc} /> : null
              }
              label={connectorProperties.name}
              actions={[
                {
                  content: intl.formatMessage(COMMON_MESSAGES.connectAccount),
                  variant: "Tertiary",
                  onAction: () => {
                    onInitiateConnection?.();
                    setConnectionInProgress(true);
                    connectByPath(connectorProperties?.connectPath);
                  },
                  loading: connectionInProgress,
                },
              ]}
            />
          }
        />
      </Flex>
    </Flex>
  );
};

const MetaAdsSelectContent = ({
  completeConnectionProps,
}: {
  completeConnectionProps: ConnectionSchemas.CompleteConnectionPropsSchema;
}) => {
  const intl = useIntl();
  return (
    <Flex direction="column" gap="8">
      <Text>{intl.formatMessage(MESSAGES.selectAccounts)}</Text>
      <PropertySelector connectorSlug={CONNECTOR_SLUG} completeConnectionProps={completeConnectionProps} />
    </Flex>
  );
};

export const MetaAdsConnect = ({ onInitiateConnection }: { onInitiateConnection?: () => void }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const completeConnection = searchParams.get("complete-connection");
  const oAuthError = searchParams.get("oauth-error");

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { completeConnectionApiUrl } = useConnectionFlowsConfig();
  const { data: completeConnectionProps, isLoading } = useCompleteConnectionProps(
    customerDomain,
    completeConnectionApiUrl,
    { enabled: !!completeConnection }
  );

  useEffect(() => {
    if (!isLoading && completeConnectionProps && completeConnectionProps.oAuthError) {
      if (completeConnectionProps.oAuthError) {
        setSearchParams({ platform: CONNECTOR_SLUG, "oauth-error": completeConnectionProps.oAuthError });
      } else if (
        !completeConnectionProps.accounts ||
        Object.keys(completeConnectionProps.accounts).length === 0 ||
        (Array.isArray(completeConnectionProps.accounts) && completeConnectionProps.accounts.length === 0)
      ) {
        setSearchParams({ platform: CONNECTOR_SLUG, "oauth-error": "no_accounts" });
      }
      // Cancel connection if there is an error to prevent hanging unconfirmed
      client.post(completeConnectionProps.cancelConnectionPath, completeConnectionProps);
    }
  }, [completeConnectionProps, isLoading, searchParams, setSearchParams]);

  if (!oAuthError && completeConnection && completeConnection === CONNECTOR_SLUG) {
    return !isLoading && !!completeConnectionProps ? (
      <MetaAdsSelectContent completeConnectionProps={completeConnectionProps} />
    ) : (
      <ConnectorPropertiesLoading connectorName={CONNECTOR_NAME} />
    );
  } else {
    return <MetaAdsConnectContent onInitiateConnection={onInitiateConnection} />;
  }
};
